import React, { useEffect, useState } from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';

interface SavingsCalculatorProps {
  totalGenerated: number;
}

const SavingsCalculator: React.FC<SavingsCalculatorProps> = ({ totalGenerated }) => {
  const { convexUser } = useBetterAuthConvexAuth();
  const getOctopusRate = useAction(api.tariffs.getOctopusRate);
  const setMyTariff = useMutation(api.tariffs.setMyTariff);

  const [pricePerKwh, setPricePerKwh] = useState('');
  const [priceSource, setPriceSource] = useState<string>('manual');
  const [showResult, setShowResult] = useState(false);
  const [showError, setShowError] = useState(false);
  const [savings, setSavings] = useState(0);
  const [postcode, setPostcode] = useState('');
  const [needPostcode, setNeedPostcode] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Prefill from the customer's saved tariff once loaded.
  useEffect(() => {
    if (convexUser?.tariffPencePerKwh && pricePerKwh === '') {
      setPricePerKwh(String(convexUser.tariffPencePerKwh));
      setPriceSource(convexUser.tariffSource || 'manual');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convexUser?.tariffPencePerKwh]);

  const handleCalculate = () => {
    const price = parseFloat(pricePerKwh);
    const isValidPrice = Number.isFinite(price) && price > 0;
    if (!isValidPrice) {
      setSavings(0);
      setShowError(true);
      setShowResult(false);
      return;
    }

    const totalSavings = totalGenerated * price;
    setSavings(totalSavings);
    setShowError(price <= 5);
    setShowResult(true);
  };

  const handleFetchOctopus = async () => {
    setStatusMessage('');
    setFetching(true);
    try {
      // Client-side timeout so a stalled lookup re-enables the button promptly.
      const result = await Promise.race([
        getOctopusRate(
          needPostcode && postcode.trim() ? { postcode: postcode.trim() } : {}
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 15000)
        ),
      ]);
      if (result?.success) {
        setPricePerKwh(String(result.pencePerKwh));
        setPriceSource(`octopus:${result.productCode}`);
        setNeedPostcode(false);
        setStatusMessage(
          `Current ${result.productName} rate for your area (${result.postcodeUsed}): ${result.pencePerKwh}p per kWh. Adjust it if your own tariff differs.`
        );
      } else if (result?.error === 'postcode_required' || result?.error === 'region_not_found') {
        setNeedPostcode(true);
        setStatusMessage('Please enter your postcode so we can look up the rate for your region.');
      } else {
        setStatusMessage('Sorry, we could not fetch the current rate right now. Please enter your rate manually.');
      }
    } catch {
      setStatusMessage('Sorry, we could not fetch the current rate right now. Please enter your rate manually.');
    } finally {
      setFetching(false);
    }
  };

  const handleSaveRate = async () => {
    const price = parseFloat(pricePerKwh);
    if (!Number.isFinite(price) || price <= 0) {
      setShowError(true);
      return;
    }
    setStatusMessage('');
    setSaving(true);
    try {
      const result = await setMyTariff({ pencePerKwh: price, source: priceSource });
      if (result?.success) {
        setStatusMessage('Your rate has been saved and will be remembered next time.');
      } else {
        setStatusMessage(result?.error || 'Unable to save your rate. Please try again.');
      }
    } catch {
      setStatusMessage('Unable to save your rate. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold font-sans mb-3 sm:mb-4">CHECK HOW MUCH YOU COULD HAVE SAVED!</h2>

      <p className="text-sm sm:text-base mb-4 sm:mb-6">
        Enter your current energy price (cost per kWh) and see how much you may be saving on your energy bills.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-bold text-gray-700 mb-2">
          Enter your energy cost per kWh
        </label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <input
            type="text"
            value={pricePerKwh}
            onChange={(e) => {
              setPricePerKwh(e.target.value);
              setPriceSource('manual');
            }}
            className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
            placeholder="e.g. 29"
          />
          <span className="text-sm sm:text-base text-gray-600">Pence per kWh</span>
          <button
            type="button"
            onClick={handleFetchOctopus}
            disabled={fetching}
            className="text-sm border border-shade-green-600 text-shade-green-700 px-3 py-2 rounded-md hover:bg-shade-green-50 transition-colors disabled:opacity-50"
          >
            {fetching ? 'Fetching...' : 'Use current Octopus rate'}
          </button>
        </div>
        {needPostcode && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-shade-green-500"
              placeholder="Postcode"
            />
            <button
              type="button"
              onClick={handleFetchOctopus}
              disabled={fetching || !postcode.trim()}
              className="text-sm border border-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Look up
            </button>
          </div>
        )}
        {statusMessage && (
          <p className="mt-2 text-sm text-gray-600" aria-live="polite">{statusMessage}</p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          onClick={handleCalculate}
          className="w-full sm:w-auto bg-[#16A34A]  text-white px-6 py-3 rounded-md hover:bg-shade-green-700 transition-colors"
        >
          Calculate
        </button>
        <button
          type="button"
          onClick={handleSaveRate}
          disabled={saving}
          className="w-full sm:w-auto border border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save my rate'}
        </button>
      </div>

      {showError && (
        <div className="mt-6 p-4 bg-yellow-100 rounded-lg">
          <p className="text-lg font-medium font-sans text-yellow-800">
            Correct entered value.
          </p>
          <p className="text-sm text-yellow-700 mt-2">
            This energy cost seems a litle low, are you sure it's correct?
          </p>
        </div>
      )}
      {showResult && (
        <div className="mt-6 p-4 bg-shade-green-50 rounded-lg">
          <p className="text-lg font-medium font-sans text-shade-green-800">
            Total Savings: £{(savings / 100).toFixed(2)}
          </p>
          <p className="text-sm text-shade-green-700 mt-2">
            Based on {totalGenerated.toLocaleString(undefined, { maximumFractionDigits: 2 })} kWh
            generated at {parseFloat(pricePerKwh).toFixed(2)}p per kWh*
          </p>
          <p className="text-xs text-shade-green-700 mt-1">
            *Savings depend on the rate you pay your energy supplier — check your bill for your exact unit rate.
          </p>
        </div>
      )}

      <div className="mt-6 text-xs sm:text-sm text-gray-600">
        <p className="mb-2">
          This calculator provides guidance on how much you could save on your electricity bills by using all of the FREE electricity provided by
          the solar panels situated at your property.
        </p>
        <p className="mb-2">
          Please note that any savings made is not shown on your energy bills. This is the energy you could use generated from the solar
          panels instead of paying for it from your energy supplier.
        </p>
        <details className="mt-3">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">Disclaimer</summary>
          <p className="mt-2 text-xs text-gray-500 leading-relaxed">
            ASGM assumes no responsibility or liability for errors or omission in the content of this site. The information contained in this site is provided on an "as is"
            basis with no guarantee of completeness or accuracy. This information is for educational purposes only and is in no way meant to suggest or discourage any
            decision. No products or services are recommended. No financial advice in any form is given. ASGM have no responsibility and accepts no liability against the
            production or lack of production of energy from the solar panels or the usage thereof.
          </p>
        </details>
      </div>
    </div>
  );
};

export default SavingsCalculator;
